import React from 'react';
import {
  ExclamationTriangleIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Button, Card, Spinner } from './UIComponents';

interface BaseStateProps {
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}

interface LoadingStateProps {
  title?: string;
  message?: string;
  className?: string;
}

const StateWrapper: React.FC<{
  icon: React.ReactNode;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}> = ({ icon, title, message, actionLabel, onAction, className = '' }) => (
  <Card className={`px-4 py-7 text-center sm:px-5 sm:py-8 ${className}`}>
    <div className="mx-auto mb-3 h-10 w-10 text-primary-light-400">{icon}</div>
    <h2 className="mb-1.5 text-base font-semibold text-primary-light-100 sm:text-lg">{title}</h2>
    <p className="text-sm text-primary-light-300">{message}</p>
    {actionLabel && onAction && (
      <Button variant="neutral" size="sm" className="mt-4" onClick={onAction}>
        {actionLabel}
      </Button>
    )}
  </Card>
);

export const PageLoadingState: React.FC<LoadingStateProps> = ({
  title = 'Ładowanie danych',
  message = 'Poczekaj chwilę, przygotowujemy widok.',
  className = '',
}) => (
  <div className={`flex flex-col items-center justify-center py-10 ${className}`}>
    <Spinner size="lg" color="primary" className="mb-3" />
    <p className="font-medium text-primary-light-100">{title}</p>
    <p className="mt-1 text-sm text-primary-light-300">{message}</p>
  </div>
);

export const PageErrorState: React.FC<BaseStateProps> = ({
  title,
  message,
  actionLabel,
  onAction,
  className = '',
}) => (
  <StateWrapper
    icon={<ExclamationTriangleIcon className="w-12 h-12 mx-auto" />}
    title={title}
    message={message}
    actionLabel={actionLabel}
    onAction={onAction}
    className={className}
  />
);

export const PageEmptyState: React.FC<BaseStateProps> = ({
  title,
  message,
  actionLabel,
  onAction,
  className = '',
}) => (
  <StateWrapper
    icon={<InboxIcon className="w-12 h-12 mx-auto" />}
    title={title}
    message={message}
    actionLabel={actionLabel}
    onAction={onAction}
    className={className}
  />
);

export const PageNoResultsState: React.FC<BaseStateProps> = ({
  title,
  message,
  actionLabel,
  onAction,
  className = '',
}) => (
  <StateWrapper
    icon={<MagnifyingGlassIcon className="w-12 h-12 mx-auto" />}
    title={title}
    message={message}
    actionLabel={actionLabel}
    onAction={onAction}
    className={className}
  />
);

export const PageNotFoundState: React.FC<BaseStateProps> = ({
  title,
  message,
  actionLabel,
  onAction,
  className = '',
}) => (
  <StateWrapper
    icon={<DocumentMagnifyingGlassIcon className="w-12 h-12 mx-auto" />}
    title={title}
    message={message}
    actionLabel={actionLabel}
    onAction={onAction}
    className={className}
  />
);
